import * as React from 'react'

import {
  Box,
  SkeletonComponent,
  Table,
  useTableDensity,
} from '@factorialco/deprecated-design-system'

import { selectableColumnWidth } from 'modules/core/components/DataTable/constants'
import { Selectable } from 'modules/core/components/DataTable/core/types'
import useSticky from 'modules/core/components/DataTable/hooks/useSticky'
import type { HeadCell } from 'modules/core/components/DataTable/types'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'

interface Props<S extends string> {
  header: HeadCell<S>[]
  itemsPerPage: number
  withBody?: boolean
  selectable?: Selectable
}

function SkeletonContent<T, S extends string>({
  header,
  itemsPerPage,
  selectable,
}: Omit<Props<S>, 'withBody'>) {
  const uniqueId = React.useId()
  const getStickyProp = useSticky()
  const tableDensity = useTableDensity()

  // We memoize the widths to avoid skeletons changing their width on rerenders
  const cellSkeletonsWidths = React.useRef<Record<string, number>>({}).current
  const getCellSkeletonWidth = useUnsafeCallback(
    (key: string) => {
      let width = cellSkeletonsWidths[key]

      if (!width) {
        width = Math.floor((Math.random() * 0.4 + 0.4) * 100)
        cellSkeletonsWidths[key] = width
      }

      return width
    },
    [cellSkeletonsWidths]
  )

  return (
    <>
      {Array.from({ length: itemsPerPage }).map((_, rowIndex) => {
        return (
          <Table.Row key={`${uniqueId}_${rowIndex}`}>
            {selectable && <Table.Cell width={selectableColumnWidth(tableDensity)} />}
            {header.map(({ id, width, colSpan, title, sticky }, index) => {
              const stickyProp = getStickyProp(index, header.length, !!sticky, !!selectable)
              const cellKey = `${rowIndex}_${id}`
              const randomWidth = getCellSkeletonWidth(cellKey)

              return (
                <Table.Cell key={cellKey} width={width} colSpan={colSpan} sticky={stickyProp}>
                  {title && (
                    <div style={{ width: `${randomWidth}%` }}>
                      <Box height="s24" justifyContent="center">
                        <SkeletonComponent isLoading />
                      </Box>
                    </div>
                  )}
                </Table.Cell>
              )
            })}
          </Table.Row>
        )
      })}
    </>
  )
}

function Skeleton<T, S extends string>({ withBody = true, ...otherProps }: Props<S>) {
  if (!withBody) {
    return <SkeletonContent {...otherProps} />
  }

  return (
    <Table.Body>
      <SkeletonContent {...otherProps} />
    </Table.Body>
  )
}

export default React.memo(Skeleton)
